/**
 * verdict().heuristicScore 的历史线性权重注册表。
 *
 * 重要：原书只给出象义与方向，从不给数值。即便 basis 为 textual，value 的
 * 具体幅度仍是工程刻度；basis 只说明“这个方向”有何种依据：
 * - textual：原书明文给出方向；
 * - derived：由原书具体卦例归纳方向；
 * - assigned：项目规格指定，尚无直接原文或卦例支撑。
 *
 * confirmedBy 是固定 development 切分上的审计快照，不参与计算；
 * test/report-verdict-weights.mjs 会动态重算并校验它，防止元数据陈旧。
 */

const source = (chapter, note) => Object.freeze({
  book: '增删卜易', chapter, note
});

// 仅记录 development 中“该权重被触发且整条 verdict 方向一致”的案例。
// 这是审计元数据，不参与任何计算；含义是相关而非因果。
const CONFIRMED_BY = Object.freeze({
  'state-wang': ['zsby-006', 'zsby-033', 'zsby-059'],
  'state-xiang': ['zsby-016', 'zsby-039'],
  'state-xiu': ['zsby-023', 'zsby-037'],
  'state-qiu': ['zsby-012', 'zsby-060'],
  'state-si': ['zsby-003', 'zsby-043'],
  'day-same-branch': ['zsby-012', 'zsby-016', 'zsby-043'],
  'day-generates-line': ['zsby-039'],
  'day-overcomes-line': ['zsby-003', 'zsby-037', 'zsby-060'],
  'month-break-resolvable': ['zsby-043', 'zsby-059', 'zsby-060'],
  'transform-return-overcome': ['zsby-003', 'zsby-012', 'zsby-037'],
  'transform-retreat': ['zsby-023'],
  'six-clash-primary': ['zsby-016', 'zsby-023', 'zsby-059'],
  'six-clash-changed': ['zsby-016', 'zsby-023'],
  'six-combine-primary': ['zsby-033'],
  'quiet-six-combine': ['zsby-033'],
  'triple-same-use-element': ['zsby-023'],
  'triple-generates-use': ['zsby-016', 'zsby-059'],
  'triple-overcomes-use': ['zsby-003', 'zsby-023'],
  'use-generates-triple': ['zsby-037'],
  'world-in-triple': ['zsby-016', 'zsby-023', 'zsby-037', 'zsby-059'],
  'triple-generates-world': ['zsby-003', 'zsby-016', 'zsby-023'],
  'pair-punish': ['zsby-012', 'zsby-043']
});

const weight = (group, id, value, basis, rationale, citedSource = null) => Object.freeze({
  group,
  id,
  value,
  basis,
  source: citedSource,
  rationale,
  confirmedBy: Object.freeze(CONFIRMED_BY[id] || [])
});

export const VERDICT_WEIGHTS = Object.freeze([
  // 旺相休囚死：方向见《四时旺相章》，数值幅度仍是工程刻度。
  weight('旺衰', 'state-wang', 2, 'textual', '用神旺，得令有力', source('四时旺相章第又十五', '旺相者有力，休囚者无力。')),
  weight('旺衰', 'state-xiang', 1, 'textual', '用神相，得月令所生', source('四时旺相章第又十五', '旺相者有力，休囚者无力。')),
  weight('旺衰', 'state-xiu', -1, 'textual', '用神休，生月令而泄气', source('四时旺相章第又十五', '旺相者有力，休囚者无力。')),
  weight('旺衰', 'state-qiu', -1.5, 'textual', '用神囚，克月令而无气', source('四时旺相章第又十五', '旺相者有力，休囚者无力。')),
  weight('旺衰', 'state-si', -2, 'textual', '用神死，受月令所克', source('四时旺相章第又十五', '旺相者有力，休囚者无力。')),
  weight('旺衰', 'state-neutral', 0, 'assigned', '非标准五态时的工程中性值'),

  weight('日辰', 'day-same-branch', 1.5, 'textual', '用神临日辰，得日辰扶持', source('日辰章第十七', '日辰为六爻之主宰，四时俱旺。')),
  weight('日辰', 'day-generates-line', 1, 'textual', '日辰生用神', source('日辰章第十七', '爻之衰弱，日辰能生扶合拱。')),
  weight('日辰', 'day-overcomes-line', -1.5, 'textual', '日辰克用神', source('日辰章第十七', '爻之强旺，日辰能克害刑冲。')),
  weight('特殊状态', 'month-break-persistent', -2, 'textual', '月破且静而无日辰、动爻生助，到底而破', source('月破章第二十七', '惟而不动又无日辰动爻生助者，则到底而破。')),
  weight('特殊状态', 'month-break-resolvable', -1, 'textual', '月破但发动或得生助，仍破而可解', source('月破章第二十七', '目下虽破，出月、实破或合之日则不破。')),
  weight('特殊状态', 'day-break', -2, 'textual', '衰弱静爻被日辰冲为日破', source('日辰章第十七', '冲衰弱之静爻为日破，愈加无用。')),
  weight('特殊状态', 'void', -1.5, 'textual', '旬空用神暂时无力', source('旬空章第二十六', '旬空之爻须待出空、填冲而有用。')),

  // 动变分值为项目规格指定；原书对吉神、忌神有条件差异，当前无条件线性值不冒充明文。
  weight('动变', 'transform-return-generate', 1, 'assigned', '回头生加分'),
  weight('动变', 'transform-return-overcome', -2, 'assigned', '回头克扣分'),
  weight('动变', 'transform-advance', 0.8, 'assigned', '化进神加分'),
  weight('动变', 'transform-retreat', -0.8, 'assigned', '化退神扣分'),

  weight('卦象', 'six-clash-primary', -1, 'textual', '本卦六冲主散', source('六冲章第二十', '六冲有散、有速之象。')),
  weight('卦象', 'six-clash-changed', -0.5, 'textual', '变卦六冲表示后势见散', source('六冲章第二十', '卦变六冲亦以散论，须结合所占之事。')),
  weight('卦象', 'six-combine-primary', 1, 'textual', '本卦六合主合与持久', source('六合章第十九', '万事若得三六合，诸事必得久远，有始有终。')),
  weight('卦象', 'six-combine-changed', 0.5, 'textual', '变卦六合表示后势趋合', source('六合章第十九', '六冲卦变六合、六合卦变六合皆列为合法。')),
  weight('卦象', 'six-clash-to-combine', 0, 'assigned', '六冲变六合已结构化，方向权重待核'),
  weight('卦象', 'six-combine-to-combine', 0, 'assigned', '六合变六合已结构化，方向权重待核'),
  weight('卦象', 'refrain', -1, 'assigned', '反吟主反复，暂扣一分'),
  weight('卦象', 'repetition', -0.75, 'assigned', '伏吟主滞，暂扣四分之三'),
  weight('卦象', 'quiet-six-clash', -0.5, 'assigned', '六冲而六爻安静，追加事散不成'),
  weight('卦象', 'quiet-six-combine', 0.5, 'assigned', '六合而六爻安静，追加稳定持久'),

  // 三合局对用神的线性值为项目规格指定。
  weight('三合局', 'triple-same-use-element', 1, 'assigned', '三合局与用神同五行'),
  weight('三合局', 'triple-generates-use', 1, 'assigned', '三合局生用神'),
  weight('三合局', 'triple-overcomes-use', -1, 'assigned', '三合局克用神'),
  weight('三合局', 'use-generates-triple', -0.5, 'assigned', '用神生三合局而泄'),
  weight('三合局', 'world-in-triple', 1, 'textual', '世爻在三合局中为美', source('六合章第十九', '三合其局者必要世爻在局者为美。')),
  weight('三合局', 'triple-generates-world', 0.5, 'textual', '世不在局而局生世为吉', source('六合章第十九', '世爻不在局内，须要局生世爻为吉。')),
  weight('三合局', 'triple-overcomes-world', -1, 'textual', '局克世爻为凶', source('六合章第十九', '局克世爻为凶。')),

  weight('刑', 'full-punish', -2, 'derived', '三刑全见按重刑扣分', source('zsby-020', '“月建在寅，日建在申，与巳爻子孙共作三刑”。')),
  weight('刑', 'pair-punish', -0.5, 'assigned', '两支相刑固定扣一次'),

  // 伏神路径四项均为用户规格指定的工程刻度。
  weight('伏神', 'hidden-absent', -1.5, 'assigned', '用神不上卦的基础扣分'),
  weight('伏神', 'flying-overcomes-hidden', -1, 'assigned', '飞神克伏神，伏神受制'),
  weight('伏神', 'flying-generates-hidden', 0.5, 'assigned', '飞神生伏神，伏神得助'),
  weight('伏神', 'hidden-can-release', 1, 'assigned', '伏神具备出伏条件，抵消部分不利')
]);

const BY_ID = new Map(VERDICT_WEIGHTS.map((entry) => [entry.id, entry]));
if (BY_ID.size !== VERDICT_WEIGHTS.length) throw new Error('verdict weight id 必须唯一');

export function verdictWeight(id) {
  const entry = BY_ID.get(id);
  if (!entry) throw new RangeError(`未知 verdict weight：${id}`);
  return entry;
}

export const weightValue = (id) => verdictWeight(id).value;

export const LINE_STATE_WEIGHT_IDS = Object.freeze({
  旺: 'state-wang',
  相: 'state-xiang',
  休: 'state-xiu',
  囚: 'state-qiu',
  死: 'state-si',
  平: 'state-neutral'
});

export const MONTH_BREAK_WEIGHT_IDS = Object.freeze({
  到底而破: 'month-break-persistent',
  可解: 'month-break-resolvable'
});
